/**
 * Analytics utility for econtrol-dashboard
 * Integrates with external Umami analytics system (buergerstrom.tirol)
 * Following the external integration pattern from buergerstrom project
 */

// TestMode removed - now handled by URL params in API routes

declare global {
  interface Window {
    umami?: {
      track: (eventName: string, eventData?: Record<string, any>) => void;
    };
    umamiConfig?: {
      websiteId: string;
      apiEndpoint: string;
      domain: string;
      trackingDomain: string;
      debug?: boolean;
    };
  }
}

export interface AnalyticsEvent {
  name: string;
  data?: Record<string, any>;
}

export interface EntryPointData {
  entry_point: string;
  source?: string;
  municipality?: string;
  energy_type?: string;
}

export interface EnergyAnalysisData {
  municipality: string;
  postal_code: string;
  total_potential: number;
  pv_potential: number;
  water_potential: number;
  biomass_potential: number;
  biogas_potential: number;
  euro_savings: number;
}

export interface CalculatorData {
  calculator_type: 'savings' | 'potential' | 'comparison';
  municipality: string;
  inputs: Record<string, any>;
  results: Record<string, any>;
}

class Analytics {
  private isInitialized = false;
  private debug = false;

  constructor() {
    if (typeof window !== 'undefined') {
      this.debug = process.env.NODE_ENV === 'development';
    }
  }

  /**
   * Initialize analytics tracking
   */
  initialize(): void {
    if (typeof window === 'undefined') return;

    const websiteId = process.env.NEXT_PUBLIC_UMAMI_WEBSITE_ID;
    const apiEndpoint = process.env.NEXT_PUBLIC_UMAMI_API_ENDPOINT;
    
    if (!websiteId || !apiEndpoint) {
      if (this.debug) {
        console.warn('Analytics: Missing environment variables');
      }
      return;
    }

    // Set up Umami configuration
    window.umamiConfig = {
      websiteId,
      apiEndpoint,
      domain: 'buergerstrom.tirol',
      trackingDomain: window.location.hostname,
      debug: this.debug,
    };

    this.isInitialized = true;

    if (this.debug) {
      console.log('Analytics initialized:', window.umamiConfig);
    }
  }

  /**
   * Generic event tracking
   */
  track(eventName: string, eventData: Record<string, any> = {}): void {
    if (typeof window === 'undefined' || !this.isInitialized) return;

    // Test mode is now handled by URL params in API routes
    // Umami tracking is separate from funnel tracking

    if (window.umami) {
      const enhancedData = {
        ...eventData,
        domain: window.location.hostname,
        timestamp: new Date().toISOString(),
      };

      window.umami.track(eventName, enhancedData);

      if (this.debug) {
        console.log('Analytics event:', eventName, enhancedData);
      }
    } else if (this.debug) {
      console.warn('Analytics: Umami not available, queuing event:', eventName, eventData);
    }
  }

  /**
   * Track page views
   */
  trackPageView(path?: string, title?: string): void {
    this.track('pageview', {
      url: path || window.location.pathname,
      title: title || document.title,
      referrer: document.referrer,
    });
  }

  /**
   * Track entry points to the dashboard
   */
  trackEntryPoint(data: EntryPointData): void {
    this.track('entry_point', data);
  }

  /**
   * Track municipality selection
   */
  trackMunicipalitySelect(municipality: string, postalCode: string, source: string): void {
    this.track('municipality_select', {
      municipality,
      postal_code: postalCode,
      source, // 'search', 'url', 'dropdown'
    });
  }

  /**
   * Track energy analysis viewing
   */
  trackEnergyAnalysis(data: EnergyAnalysisData): void {
    this.track('energy_analysis_view', data);
  }

  /**
   * Track calculator usage
   */
  trackCalculatorUsage(data: CalculatorData): void {
    this.track('calculator_usage', data);
  }

  /**
   * Track PDF export
   */
  trackPdfExport(municipality: string, exportType: 'infographic' | 'full_report'): void {
    this.track('pdf_export', {
      municipality,
      export_type: exportType,
    });
  }

  /**
   * Track appointment booking
   */
  trackAppointmentBooking(municipality: string, success: boolean): void {
    this.track('appointment_booking', {
      municipality,
      success,
    });
  }

  /**
   * Track chart interactions
   */
  trackChartInteraction(chartType: string, interaction: string, data?: Record<string, any>): void {
    this.track('chart_interaction', {
      chart_type: chartType,
      interaction,
      ...data,
    });
  }

  /**
   * Track button clicks
   */
  trackButtonClick(buttonName: string, location: string, additionalData?: Record<string, any>): void {
    this.track('button_click', {
      button_name: buttonName,
      location,
      ...additionalData,
    });
  }

  /**
   * Track form interactions
   */
  trackFormInteraction(formName: string, action: 'start' | 'submit' | 'error', data?: Record<string, any>): void {
    this.track('form_interaction', {
      form_name: formName,
      action,
      ...data,
    });
  }

  /**
   * Track navigation events
   */
  trackNavigation(from: string, to: string, method: 'click' | 'url' | 'back' | 'forward'): void {
    this.track('navigation', {
      from,
      to,
      method,
    });
  }

  /**
   * Track search queries
   */
  trackSearch(query: string, results: number, source: string): void {
    this.track('search', {
      query,
      results,
      source,
    });
  }

  /**
   * Track mobile vs desktop usage
   */
  trackDeviceInfo(): void {
    if (typeof window === 'undefined') return;

    const isMobile = window.innerWidth < 768;
    const deviceType = isMobile ? 'mobile' : 'desktop';
    
    this.track('device_info', {
      device_type: deviceType,
      screen_width: window.innerWidth,
      screen_height: window.innerHeight,
      user_agent: navigator.userAgent,
    });
  }

  /**
   * Track errors
   */
  trackError(error: Error, context?: string): void {
    this.track('error', {
      error_message: error.message,
      error_stack: error.stack,
      context,
      url: window.location.href,
    });
  }

  /**
   * Track performance metrics
   */
  trackPerformance(metric: string, value: number, unit: string): void {
    this.track('performance', {
      metric,
      value,
      unit,
    });
  }
}

// Create singleton instance
export const analytics = new Analytics();

// Initialize on client side
if (typeof window !== 'undefined') {
  analytics.initialize();
}

export default analytics;